// SPDX-License-Identifier: AGPL-3.0-or-later

import * as StickerPickerCommands from '@app/features/emoji/commands/StickerPickerCommands';
import StickerPicker from '@app/features/emoji/state/StickerPicker';
import type {GuildSticker} from '@app/features/expressions/models/GuildSticker';
import Guilds from '@app/features/guild/state/Guilds';
import {CloneStickerMenuItem} from '@app/features/ui/action_menu/items/CloneStickerMenuItem';
import styles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {ReverseImageSearchMenuItems} from '@app/features/ui/action_menu/items/ReverseImageSearchMenuItems';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon, ClipboardIcon, LinkIcon, StarIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
export type StickerContextMenuSticker = Readonly<
	Pick<GuildSticker, 'id' | 'guildId' | 'name' | 'description' | 'tags' | 'animated' | 'nsfw' | 'url' | 'user'>
>;
interface StickerContextMenuItemsProps {
	sticker: StickerContextMenuSticker;
	onClose: () => void;
}
const useStickerHandlers = (sticker: StickerContextMenuSticker, onClose: () => void) => {
	const {i18n, t} = useLingui();
	const stickerImageUrl = AvatarUtils.getStickerURL({id: sticker.id, animated: false, size: 320});
	const canFavorite = Boolean(sticker.guildId && Guilds.getGuild(sticker.guildId));
	const isFavorite = canFavorite ? StickerPicker.isFavorite(sticker) : false;
	const handleToggleFavorite = useCallback(() => {
		StickerPickerCommands.toggleFavorite(sticker);
	}, [sticker]);
	const handleCopyId = useCallback(() => {
		TextCopyCommands.copy(i18n, sticker.id);
		onClose();
	}, [i18n, sticker.id, onClose]);
	const handleCopyUrl = useCallback(async () => {
		await TextCopyCommands.copy(i18n, stickerImageUrl, true);
		ToastCommands.createToast({
			type: 'success',
			children: t`Link copied to clipboard`,
		});
		onClose();
	}, [i18n, t, stickerImageUrl, onClose]);
	const handleOpenInBrowser = useCallback(() => {
		void openExternalUrl(stickerImageUrl);
		onClose();
	}, [stickerImageUrl, onClose]);
	return {
		stickerImageUrl,
		canFavorite,
		isFavorite,
		handleToggleFavorite,
		handleCopyId,
		handleCopyUrl,
		handleOpenInBrowser,
	};
};
export const StickerContextMenuItems = observer(({sticker, onClose}: StickerContextMenuItemsProps) => {
	const {t} = useLingui();
	const {stickerImageUrl, canFavorite, isFavorite, handleToggleFavorite, handleCopyId} = useStickerHandlers(
		sticker,
		onClose,
	);
	return (
		<>
			<MenuGroup>
				{canFavorite && (
					<MenuItem
						icon={<StarIcon className={styles.iconSmall} weight={isFavorite ? 'fill' : 'bold'} />}
						onClick={handleToggleFavorite}
					>
						{isFavorite ? t`Unfavorite Sticker` : t`Favorite Sticker`}
					</MenuItem>
				)}
				<MenuItem icon={<ClipboardIcon className={styles.iconSmall} />} onClick={handleCopyId}>
					{t`Copy Sticker ID`}
				</MenuItem>
			</MenuGroup>
			<CloneStickerMenuItem sticker={sticker} onClose={onClose} />
			<ReverseImageSearchMenuItems
				imageUrl={stickerImageUrl}
				onClose={onClose}
				wrapInGroup
				includeCopyAndOpen
				copyLabel={t`Copy Sticker URL`}
				openLabel={t`Open Sticker in Browser`}
			/>
		</>
	);
});

StickerContextMenuItems.displayName = 'StickerContextMenuItems';
export const StickerInlineMenuItems = observer(({sticker, onClose}: StickerContextMenuItemsProps) => {
	const {t} = useLingui();
	const {
		stickerImageUrl,
		canFavorite,
		isFavorite,
		handleToggleFavorite,
		handleCopyId,
		handleCopyUrl,
		handleOpenInBrowser,
	} = useStickerHandlers(sticker, onClose);
	return (
		<MenuGroup>
			{canFavorite && (
				<MenuItem onClick={handleToggleFavorite}>{isFavorite ? t`Unfavorite Sticker` : t`Favorite Sticker`}</MenuItem>
			)}
			<MenuItem onClick={handleCopyUrl}>{t`Copy Image Link`}</MenuItem>
			<MenuItem onClick={handleOpenInBrowser}>{t`Open Image Link`}</MenuItem>
			<MenuItemSubmenu
				label={t`More Sticker Actions`}
				render={() => (
					<>
						<MenuGroup>
							{canFavorite && (
								<MenuItem
									icon={<StarIcon className={styles.iconSmall} weight={isFavorite ? 'fill' : 'bold'} />}
									onClick={handleToggleFavorite}
								>
									{isFavorite ? t`Unfavorite Sticker` : t`Favorite Sticker`}
								</MenuItem>
							)}
							<MenuItem icon={<ClipboardIcon className={styles.iconSmall} />} onClick={handleCopyId}>
								{t`Copy Sticker ID`}
							</MenuItem>
							<MenuItem icon={<LinkIcon className={styles.iconSmall} weight="bold" />} onClick={handleCopyUrl}>
								{t`Copy Sticker URL`}
							</MenuItem>
							<MenuItem icon={<ArrowSquareOutIcon className={styles.iconSmall} />} onClick={handleOpenInBrowser}>
								{t`Open Sticker in Browser`}
							</MenuItem>
						</MenuGroup>
						<CloneStickerMenuItem sticker={sticker} onClose={onClose} />
						<ReverseImageSearchMenuItems imageUrl={stickerImageUrl} onClose={onClose} wrapInGroup />
					</>
				)}
			/>
		</MenuGroup>
	);
});
StickerInlineMenuItems.displayName = 'StickerInlineMenuItems';
